import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiCheck, FiX, FiSave } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { Position } from '../types';
import { getPositions, createPosition, updatePosition, deletePosition } from '../services/positionsService';
import { useAuth } from '../context/AuthContext';
import { ListSkeleton, LoadingPill } from '../components/Skeleton';
import { useMinDelayed } from '../hooks/useMinDelayed';

const PositionsManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newPosition, setNewPosition] = useState({
    name: '',
    description: '',
    isActive: true
  });
  const [editData, setEditData] = useState<{[key: string]: Partial<Position>}>({});

  useEffect(() => {
    loadPositions();
  }, []);

  const loadPositions = async () => {
    try {
      setLoading(true);
      const data = await getPositions();
      setPositions(data);
    } catch (error) {
      toast.error('Failed to load positions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePosition = async () => {
    if (!newPosition.name.trim()) {
      toast.error('Position name is required');
      return;
    }

    try {
      const created = await createPosition(newPosition);
      setPositions([...positions, created]);
      setNewPosition({ name: '', description: '', isActive: true });
      setShowCreateForm(false);
      toast.success('Position created successfully');
    } catch (error: any) {
      if (error.response?.data?.error?.includes('already exists')) {
        toast.error('Position name already exists');
      } else {
        toast.error('Failed to create position');
      }
    }
  };

  const startEdit = (position: Position) => {
    setEditingId(position.id);
    setEditData({
      [position.id]: {
        name: position.name,
        description: position.description,
        isActive: position.isActive
      }
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleUpdatePosition = async (id: string) => {
    const updates = editData[id];
    if (!updates) return;

    if (!updates.name?.trim()) {
      toast.error('Position name is required');
      return;
    }

    try {
      const updated = await updatePosition(id, updates);
      setPositions(positions.map(p => p.id === id ? updated : p));
      setEditingId(null);
      setEditData({});
      toast.success('Position updated successfully');
    } catch (error: any) {
      if (error.response?.data?.error?.includes('already exists')) {
        toast.error('Position name already exists');
      } else {
        toast.error('Failed to update position');
      }
    }
  };

  const handleDeletePosition = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to deactivate the position "${name}"?`)) {
      return;
    }

    try {
      await deletePosition(id);
      setPositions(positions.map(p => p.id === id ? { ...p, isActive: false } : p));
      toast.success('Position deactivated successfully');
    } catch (error) {
      toast.error('Failed to deactivate position');
    }
  };

  const updateEditData = (id: string, field: string, value: any) => {
    setEditData(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const showSkeleton = useMinDelayed(loading, 500);

  if (showSkeleton) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Employee Positions</h1>
          <LoadingPill label="Loading positions" />
        </div>
        <ListSkeleton count={6} rowHeight="h-20" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employee Positions</h1>
          <p className="text-gray-600 mt-2">
            {user?.role === 'viewer' 
              ? 'View employee positions that appear in the application form' 
              : 'Manage employee positions that appear in the application form'}
          </p>
        </div>
        {user?.role !== 'viewer' && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <FiPlus className="w-4 h-4" />
            <span>Add Position</span>
          </button>
        )}
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Add New Position</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Position Name *
              </label>
              <input
                type="text"
                value={newPosition.name}
                onChange={(e) => setNewPosition(prev => ({ ...prev, name: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Software Engineer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                value={newPosition.description}
                onChange={(e) => setNewPosition(prev => ({ ...prev, description: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Position description (optional)"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newPosition.isActive}
                onChange={(e) => setNewPosition(prev => ({ ...prev, isActive: e.target.checked }))}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Active position</span>
            </label>
          </div>
          <div className="flex space-x-4 mt-6">
            <button
              onClick={handleCreatePosition}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
            >
              <FiSave className="w-4 h-4" />
              <span>Save Position</span>
            </button>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setNewPosition({ name: '', description: '', isActive: true });
              }}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md flex items-center space-x-2"
            >
              <FiX className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      )}

      {/* Positions Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Position Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {positions.map((position) => (
                <tr key={position.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === position.id ? (
                      <input
                        type="text"
                        value={editData[position.id]?.name || ''}
                        onChange={(e) => updateEditData(position.id, 'name', e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900">{position.name}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingId === position.id ? (
                      <input
                        type="text"
                        value={editData[position.id]?.description || ''}
                        onChange={(e) => updateEditData(position.id, 'description', e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{position.description || '-'}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === position.id ? (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editData[position.id]?.isActive ?? position.isActive}
                          onChange={(e) => updateEditData(position.id, 'isActive', e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm">Active</span>
                      </label>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        position.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {position.isActive ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(position.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {user?.role === 'viewer' ? (
                      <span className="text-gray-400 text-xs">View only</span>
                    ) : editingId === position.id ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleUpdatePosition(position.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Save changes"
                        >
                          <FiCheck className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-gray-600 hover:text-gray-900"
                          title="Cancel edit"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEdit(position)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit position"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        {position.isActive && (
                          <button
                            onClick={() => handleDeletePosition(position.id, position.name)}
                            className="text-red-600 hover:text-red-900"
                            title="Deactivate position"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {positions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No positions found. Add your first position to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PositionsManagementPage;